import { useState, useEffect } from 'react';
import { MenuService } from '../services/menuService';

// Default menu settings - always available as fallback
const defaultMenuSettings = {
  show_homepage: true,
  show_about_us: true,
  show_services: true,
  show_service_details: true,
  show_projects_gallery: true,
  show_resources_hub: true,
  show_e_commerce_store: true,
  show_smart_contact: true,
  show_customer_portal: true,
  show_admin_dashboard: true,
  show_payment_checkout: true,
  show_payment_status_tracking: true,
  show_login: true
};

export const useMenuSettings = () => {
  const [menuSettings, setMenuSettings] = useState(defaultMenuSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking'); // checking, online, offline

  const loadMenuSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      setConnectionStatus('checking');

      // Check connection first
      const isConnected = await MenuService?.checkConnection();
      setConnectionStatus(isConnected ? 'online' : 'offline');

      const settings = await MenuService?.getMenuSettings();
      setMenuSettings({ ...defaultMenuSettings, ...settings });
    } catch (err) {
      console.warn('Menu settings load error:', err);
      setError(err);
      setConnectionStatus('offline');
      // Ensure we always have workable menu settings
      setMenuSettings(defaultMenuSettings);
    } finally {
      setLoading(false);
    }
  };

  const updateMenuSettings = async (newSettings) => {
    try {
      setError(null);
      const updatedSettings = await MenuService?.updateMenuSettings(newSettings);
      setMenuSettings({ ...defaultMenuSettings, ...updatedSettings });
      setConnectionStatus('online');
      return updatedSettings;
    } catch (err) {
      console.error('Failed to update menu settings:', err);
      setError(err);
      setConnectionStatus('offline');
      throw err;
    }
  };

  const retryConnection = async () => {
    await loadMenuSettings();
  };

  // Load settings on mount
  useEffect(() => {
    loadMenuSettings();
  }, []);

  // Auto-retry on network reconnection
  useEffect(() => {
    const handleOnline = () => {
      if (connectionStatus === 'offline') {
        console.info('Network reconnected - retrying menu settings load...');
        loadMenuSettings();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [connectionStatus]);

  return {
    menuSettings,
    loading,
    error,
    connectionStatus,
    updateMenuSettings,
    retryConnection,
    // Helper methods for checking visibility
    isVisible: (menuItem) => menuSettings?.[`show_${menuItem}`] !== false,
    // Safe getter that always returns boolean
    getVisibility: (menuItem) => Boolean(menuSettings?.[`show_${menuItem}`]),
    // Direct boolean properties that Header component expects
    isShopVisible: Boolean(menuSettings?.show_e_commerce_store),
    isGalleryVisible: Boolean(menuSettings?.show_projects_gallery),
    isResourcesVisible: Boolean(menuSettings?.show_resources_hub),
    isServicesVisible: Boolean(menuSettings?.show_services),
    isContactVisible: Boolean(menuSettings?.show_smart_contact),
    isAboutVisible: Boolean(menuSettings?.show_about_us),
    isHomepageVisible: Boolean(menuSettings?.show_homepage),
    isCustomerPortalVisible: Boolean(menuSettings?.show_customer_portal),
    isAdminDashboardVisible: Boolean(menuSettings?.show_admin_dashboard),
    isLoginVisible: Boolean(menuSettings?.show_login),
    isPaymentCheckoutVisible: Boolean(menuSettings?.show_payment_checkout),
    isPaymentStatusVisible: Boolean(menuSettings?.show_payment_status_tracking),
    isServiceDetailsVisible: Boolean(menuSettings?.show_service_details)
  };
};